import { useEffect, useState, useRef } from 'react'
import { doc, updateDoc } from 'firebase/firestore'
import FirestoreUser, {
  AdditionalDetails,
} from '../../../../../types/FirestoreUser'
import useIsMine from '../../../../../hooks/useIsMine'
import { useSubmitAdditionalDetails, useUpdateAdditionalDetails } from './index'
import debounce from '../../../../../utils/debounce'
import deepEqual from '../../../../../utils/deepEqual'
import {
  removeUndefinedFields,
  validateAdditionalDetailsComplete,
} from '../utils'
import { getEffectiveServices } from '../../../../../utils/firebaseServiceContext'

interface UseAdditionalDetailsFormProps {
  uid: string
  firestoreUser: FirestoreUser
}

/**
 * Add the authoritative P2 completion value to the details written by autosave.
 * Clinical-trial participation remains profile data, but is not a P2 requirement.
 */
export const withAdditionalDetailsCompletion = (
  additionalDetails: Partial<AdditionalDetails>,
  extraInfoRequired: boolean,
): Partial<AdditionalDetails> => {
  const isComplete = validateAdditionalDetailsComplete(
    additionalDetails,
    extraInfoRequired,
  )
  const detailsToSave = { ...additionalDetails }

  // Only PALS profiles have a P2 completion state.
  if (isComplete !== undefined) {
    detailsToSave.isComplete = isComplete
  }

  return detailsToSave
}

export const useAdditionalDetailsForm = ({
  uid,
  firestoreUser,
}: UseAdditionalDetailsFormProps) => {
  const isMine = useIsMine(uid)

  // Track the stable additionalDetails using state with deep equality comparison
  const [_additionalDetails, set_additionalDetails] = useState<
    Partial<AdditionalDetails> | undefined
  >(firestoreUser?.additionalDetails)

  // Update only if data content changed (not just reference)
  useEffect(() => {
    const newData = firestoreUser?.additionalDetails
    const isEqual = deepEqual(_additionalDetails, newData)

    if (!isEqual) {
      set_additionalDetails(newData)
    }
  }, [firestoreUser?.additionalDetails])

  const [additionalDetails, setAdditionalDetails] = useState<
    Partial<AdditionalDetails>
  >(_additionalDetails || { symptoms: [] })

  // extraInfoRequired is true if the user is a PALS. This is used to determine if fields should be required.
  const extraInfoRequired =
    !!firestoreUser.profile?.diagnosis &&
    !['medical', 'administrator'].includes(firestoreUser.profile.diagnosis)

  const update = useUpdateAdditionalDetails(setAdditionalDetails)

  // Track initial load to skip autosave on mount
  const isInitializing = useRef(true)
  // Track the last saved/synced state from Firestore to avoid feedback loops
  const lastSyncedState = useRef<Partial<AdditionalDetails> | null>(null)
  // Track if we've done the initial sync
  const hasInitialSynced = useRef(false)

  useEffect(() => {
    if (_additionalDetails && !hasInitialSynced.current) {
      const areEqual = deepEqual(additionalDetails, _additionalDetails)

      if (!areEqual) {
        lastSyncedState.current = _additionalDetails
        update(_additionalDetails)
      } else {
        lastSyncedState.current = _additionalDetails
      }

      hasInitialSynced.current = true
    } else if (_additionalDetails) {
      // Update lastSyncedState for comparison purposes
      lastSyncedState.current = _additionalDetails
    }
    // eslint-disable-next-line
  }, [_additionalDetails])

  const { submit, loading, saveSilently } = useSubmitAdditionalDetails(
    uid,
    additionalDetails,
    isMine,
  )

  // Debounced autosave effect - triggers 1500ms after last change
  // Only autosave for specific fields (dropdowns), not occupations/trials which manage their own saves
  useEffect(() => {
    if (isInitializing.current) {
      isInitializing.current = false
      return
    }

    if (!isMine || loading) {
      return
    }

    // Compare current state with last synced state to avoid saving unchanged data (use deepEqual to ignore property order)
    const statesMatch =
      lastSyncedState.current &&
      deepEqual(additionalDetails, lastSyncedState.current)

    if (statesMatch) {
      return
    }

    // Check if only dropdown fields changed (not occupations/clinicalTrials which save themselves)
    const currentCopy = { ...additionalDetails }
    const syncedCopy = { ...lastSyncedState.current }
    delete currentCopy.occupations
    delete currentCopy.clinicalTrials
    delete syncedCopy.occupations
    delete syncedCopy.clinicalTrials

    const dropdownFieldsChanged = !deepEqual(currentCopy, syncedCopy)

    if (!dropdownFieldsChanged) {
      // Update lastSyncedState so we don't keep checking
      lastSyncedState.current = additionalDetails
      return
    }

    const debouncedSave = debounce(() => {
      const detailsToSave = withAdditionalDetailsCompletion(
        additionalDetails,
        extraInfoRequired,
      )

      // Clean undefined values before saving to Firestore
      const cleanedDetails = removeUndefinedFields(detailsToSave)
      console.log('Saving cleaned details:', cleanedDetails)

      // Save with isComplete included
      const userRef = doc(getEffectiveServices().firestore, 'users', uid)
      updateDoc(userRef, {
        additionalDetails: cleanedDetails,
      })

      // Update lastSyncedState with the data that was saved
      lastSyncedState.current = detailsToSave
    }, 1500)

    debouncedSave.run()

    return () => {
      debouncedSave.flush()
    }
    // eslint-disable-next-line
  }, [additionalDetails, isMine, loading])

  return {
    additionalDetails,
    setAdditionalDetails,
    extraInfoRequired,
    update,
    submit,
    loading,
    isMine,
  }
}

export default useAdditionalDetailsForm
